import { useCallback, useEffect, useRef, useState } from 'react';
import type { Design } from '../design/schema';
import { ApiError, createDesign, updateDesign } from '../services/api';
import type { Action } from './store';

export type SyncStatus = 'local' | 'saving' | 'saved' | 'error';
export interface SyncState {
  status: SyncStatus;
  /** Save to the server now (creating the design if it has no id yet) and return its id. */
  saveNow: () => Promise<string>;
  /** Tell the sync that this design is already what the server has (just loaded). */
  markSaved: (design: Design) => void;
}

const fingerprint = (d: Design) => JSON.stringify({ ...d, id: null });

/**
 * Once a design has been saved to the server it keeps itself in sync: edits are pushed
 * after a short pause. Before that it only lives in this browser (the local draft).
 */
export function useCloudSync(design: Design, locked: boolean, dispatch: (a: Action) => void): SyncState {
  const [status, setStatus] = useState<SyncStatus>(design.id ? 'saved' : 'local');
  const saved = useRef<string | null>(design.id ? fingerprint(design) : null);
  const latest = useRef({ design, locked });
  latest.current = { design, locked };
  const inFlight = useRef<Promise<string> | null>(null);

  const push = useCallback(async (): Promise<string> => {
    // One save at a time; a caller arriving mid-save gets a fresh save queued behind it.
    if (inFlight.current) await inFlight.current.catch(() => {});
    const run = (async () => {
      const { design: d, locked: isLocked } = latest.current;
      const print = fingerprint(d);
      if (d.id && !isLocked && print === saved.current) return d.id;
      setStatus('saving');
      try {
        let record;
        if (d.id && !isLocked) {
          try {
            record = await updateDesign(d.id, d);
          } catch (e) {
            // Ordered in the meantime: the original is frozen, so carry on in a copy.
            if (e instanceof ApiError && e.status === 423) record = await createDesign({ ...d, id: null });
            else throw e;
          }
        } else {
          record = await createDesign({ ...d, id: null });
        }
        saved.current = print;
        if (record.id !== d.id || isLocked) {
          dispatch({ type: 'assignId', id: record.id, locked: false });
          const url = new URL(location.href);
          url.searchParams.set('design', record.id);
          history.replaceState(null, '', url);
        }
        setStatus('saved');
        return record.id;
      } catch (e) {
        setStatus('error');
        throw e;
      }
    })();
    inFlight.current = run;
    try {
      return await run;
    } finally {
      if (inFlight.current === run) inFlight.current = null;
    }
  }, [dispatch]);

  useEffect(() => {
    if (!design.id || locked) {
      if (!design.id) setStatus('local');
      return;
    }
    if (fingerprint(design) === saved.current) return;
    const timer = setTimeout(() => push().catch(() => {}), 1500);
    return () => clearTimeout(timer);
  }, [design, locked, push]);

  const markSaved = useCallback((d: Design) => {
    saved.current = fingerprint(d);
    setStatus('saved');
  }, []);

  return { status, saveNow: push, markSaved };
}
